import { useEffect, useMemo, useRef } from "react";
import { FieldErrors } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  clearFormValidity,
  resetAllValidities,
  setFormValidity,
} from "../../redux/slices/formValiditySlice/formValiditySlice";

interface UseSyncFormValidityProps {
  formKey: string;
  mandatoryFields: string[];
  errors: FieldErrors;
  clearOnUnmount?: boolean;
  resetAllOnUnmount?: boolean;
  isMobile: boolean;
}

// Lightweight nested value getter
export const getNestedValue = <T = any>(
  obj: any,
  path: string,
): T | undefined => {
  return path
    .replace(/\[(\d+)]/g, ".$1")
    .split(".")
    .reduce((acc, key) => acc?.[key], obj);
};

export const useSyncFormValidity = ({
  formKey,
  mandatoryFields,
  errors,
  clearOnUnmount = true,
  resetAllOnUnmount = false,
  isMobile = true,
}: UseSyncFormValidityProps) => {
  const dispatch = useDispatch();
  const prevIsValidRef = useRef<boolean | undefined>();

  // Check validity based on presence of errors in mandatory fields
  const currentIsValid = useMemo(() => {
    if (!isMobile || !mandatoryFields.length) return undefined;

    return !mandatoryFields.some((field) => !!getNestedValue(errors, field));
  }, [mandatoryFields, errors, isMobile]);

  // Update validity in Redux only if it has changed
  useEffect(() => {
    if (!isMobile || currentIsValid === undefined) return;

    if (prevIsValidRef.current !== currentIsValid) {
      dispatch(setFormValidity({ formKey, isValid: currentIsValid }));
      prevIsValidRef.current = currentIsValid;
    }
  }, [currentIsValid, formKey, isMobile, dispatch]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (resetAllOnUnmount) {
        dispatch(resetAllValidities());
      } else if (clearOnUnmount) {
        dispatch(clearFormValidity({ formKey }));
      }
    };
  }, [formKey, dispatch, clearOnUnmount, resetAllOnUnmount]);
};
